import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { IconArrowRight, IconPackage, IconCheck } from "@tabler/icons-react";
import { postRequest, getRequest } from "services/api";
import Swal from "sweetalert2";
import { useAuth } from "hooks/useAuth";
import AsyncSelect from "react-select/async";

const TransferModal = ({
  show,
  onHide,
  selectedItem,
  onSuccess,
  onError,
  warehouses = [],
}) => {
  const { user } = useAuth();
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  // Helper function to get current local date time in YYYY-MM-DDTHH:mm format
  const getCurrentLocalDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [transferData, setTransferData] = useState({
    quantity: 0,
    fromWarehouseId: "",
    toWarehouseId: "",
    responsibleStaffId: "", // This should be set from current user
    notes: "",
    transferDate: getCurrentLocalDateTime(), // Current local datetime in YYYY-MM-DDTHH:mm format
    price: 0, // Price per unit
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && selectedItem) {
      // Reset form
      setSelectedMechanic(null);
      setTransferData({
        quantity: 0,
        fromWarehouseId: warehouses.length > 0 ? warehouses[0].id : "",
        toWarehouseId: warehouses.length > 1 ? warehouses[1].id : "",
        responsibleStaffId: "", // TODO: Get from current user context
        notes: "",
        transferDate: getCurrentLocalDateTime(),
        price: parseFloat(selectedItem.price) || 0,
      });
      setError(null);
    }
  }, [show, selectedItem, warehouses]);

  const handleInputChange = (field, value) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (transferData.quantity <= 0) {
      setError("Quantity must be greater than 0");
      return;
    }

    if (transferData.fromWarehouseId === transferData.toWarehouseId) {
      setError("Source and destination warehouse must be different");
      return;
    }

    // Check if source warehouse has enough stock
    const sourceWarehouseStock = selectedItem.warehouse_stocks?.find(
      (ws) => ws.warehouse_id === transferData.fromWarehouseId,
    );
    const sourceStock = sourceWarehouseStock ? sourceWarehouseStock.stock : 0;

    if (transferData.quantity > sourceStock) {
      const warehouseName =
        warehouses.find((w) => w.id === transferData.fromWarehouseId)?.name ||
        "Selected";
      setError(
        `Insufficient stock in ${warehouseName} warehouse. Available: ${sourceStock} ${selectedItem.unit}`,
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const responsibleStaffId =
        transferData.responsibleStaffId ||
        user?.id ||
        user?.staff_id ||
        "";

      const payload = {
        from_warehouse_id: transferData.fromWarehouseId,
        to_warehouse_id: transferData.toWarehouseId,
        responsible_staff_id: responsibleStaffId,
        notes: transferData.notes || "Stock transfer between warehouses",
        transfer_date: transferData.transferDate,
        items: [
          {
            inventory_id: selectedItem.id,
            quantity: transferData.quantity,
            price: transferData.price,
          },
        ],
      };

      const response = await postRequest("/stock-transfers", payload);

      if (response.success) {
        Swal.fire(
          "Success!",
          "Stock transfer completed successfully.",
          "success",
        );
        onSuccess && onSuccess();
        onHide();
      } else {
        throw new Error(response.message || "Transfer failed");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to transfer stock";
      setError(errorMessage);
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedItem) return null;

  const sourceWarehouseStock = selectedItem.warehouse_stocks?.find(
    (ws) => ws.warehouse_id === transferData.fromWarehouseId,
  );
  const sourceStock = sourceWarehouseStock ? sourceWarehouseStock.stock : 0;

  const destinationWarehouseStock = selectedItem.warehouse_stocks?.find(
    (ws) => ws.warehouse_id === transferData.toWarehouseId,
  );
  const destinationStock = destinationWarehouseStock
    ? destinationWarehouseStock.stock
    : 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconPackage size={20} style={{ marginRight: "8px" }} />
          Transfer Stock
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: "20px" }}>
          <strong>Item:</strong> {selectedItem.name}
        </div>

        {error && (
          <Alert variant="danger" style={{ marginBottom: "20px" }}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>From Warehouse</Form.Label>
                <Form.Select
                  value={transferData.fromWarehouseId}
                  onChange={(e) =>
                    handleInputChange("fromWarehouseId", e.target.value)
                  }
                >
                  <option value="">Select Source Warehouse</option>
                  {warehouses.map((warehouse) => {
                    const warehouseStock = selectedItem.warehouse_stocks?.find(
                      (ws) => ws.warehouse_id === warehouse.id,
                    );
                    const stock = warehouseStock ? warehouseStock.stock : 0;
                    return (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({stock} {selectedItem.unit})
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col
              md={2}
              className="d-flex align-items-center justify-content-center"
            >
              <IconArrowRight size={24} style={{ color: "#6b7280" }} />
            </Col>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>To Warehouse</Form.Label>
                <Form.Select
                  value={transferData.toWarehouseId}
                  onChange={(e) =>
                    handleInputChange("toWarehouseId", e.target.value)
                  }
                >
                  <option value="">Select Destination Warehouse</option>
                  {warehouses.map((warehouse) => {
                    const warehouseStock = selectedItem.warehouse_stocks?.find(
                      (ws) => ws.warehouse_id === warehouse.id,
                    );
                    const stock = warehouseStock ? warehouseStock.stock : 0;
                    return (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({stock} {selectedItem.unit})
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Quantity to Transfer</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="1"
                max={sourceStock}
                value={transferData.quantity}
                onChange={(e) =>
                  handleInputChange("quantity", parseInt(e.target.value) || 0)
                }
                placeholder="Enter quantity"
              />
              <InputGroup.Text>{selectedItem.unit}</InputGroup.Text>
            </InputGroup>
            <Form.Text className="text-muted">
              Available in {transferData.fromWarehouse}: {sourceStock}{" "}
              {selectedItem.unit}
            </Form.Text>
          </Form.Group>

          {/* <Form.Group className="mb-3">
            <Form.Label>Price per Unit</Form.Label>
            <Form.Control
              type="text"
              readOnly
              value={`Rp ${transferData.price.toLocaleString("id-ID")}`}
              style={{ backgroundColor: "#e9ecef" }}
            />
          </Form.Group> */}

          <Form.Group className="mb-3">
            <Form.Label>Penanggung Jawab (Mekanik)</Form.Label>
            <AsyncSelect classNamePrefix="react-select" className="react-select-container"
              loadOptions={async (inputValue) => {
                try {
                  const response = await getRequest("/user?all=1&include=roles");
                  if (response.success) {
                    return response.data
                      .filter(
                        (u) =>
                          u.roles?.some((role) => role.name === "mekanik") &&
                          u.name.toLowerCase().includes(inputValue.toLowerCase())
                      )
                      .map((u) => ({
                        value: u.id,
                        label: u.name,
                      }));
                  }
                  return [];
                } catch (error) {
                  console.error("Error loading mechanics:", error);
                  return [];
                }
              }}
              value={selectedMechanic}
              onChange={(selected) => {
                handleInputChange(
                  "responsibleStaffId",
                  selected ? selected.value : ""
                );
                setSelectedMechanic(selected);
              }}
              isSearchable
              placeholder="Pilih staff penanggung jawab..."
              isClearable
              defaultOptions={true}
              cacheOptions={true}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Transfer Date & Time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={transferData.transferDate}
              onChange={(e) =>
                handleInputChange("transferDate", e.target.value)
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={transferData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="e.g., Transfer stock untuk kebutuhan service mingguan"
            />
          </Form.Group>

          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "5px",
            }}
          >
            <strong>Transfer Summary:</strong>
            <div>
              Transfer {transferData.quantity} {selectedItem.unit} from{" "}
              {warehouses.find((w) => w.id === transferData.fromWarehouseId)
                ?.name || "Source"}{" "}
              to{" "}
              {warehouses.find((w) => w.id === transferData.toWarehouseId)
                ?.name || "Destination"}
            </div>
            {/* <div>
              Price per unit: Rp {transferData.price.toLocaleString("id-ID")}
            </div>
            <div>
              Total value: Rp{" "}
              {(transferData.quantity * transferData.price).toLocaleString(
                "id-ID",
              )}
            </div> */}
            <div>
              After transfer:{" "}
              {warehouses.find((w) => w.id === transferData.fromWarehouseId)
                ?.name || "Source"}{" "}
              will have {sourceStock - transferData.quantity}{" "}
              {selectedItem.unit}
            </div>
            <div>
              After transfer:{" "}
              {warehouses.find((w) => w.id === transferData.toWarehouseId)
                ?.name || "Destination"}{" "}
              will have {destinationStock + transferData.quantity}{" "}
              {selectedItem.unit}
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || transferData.quantity <= 0}
        >
          {loading ? (
            "Transferring..."
          ) : (
            <>
              <IconCheck size={16} style={{ marginRight: "8px" }} />
              Transfer Stock
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferModal;
